/**
 * Formatters for the admin console.
 *
 * `Intl` instances are built once at module scope: constructing a
 * DateTimeFormat is the expensive part, and the meetings table formats several
 * hundred timestamps per page render.
 */

export const EM_DASH = '—';

const DATE_TIME = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const DATE_ONLY = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const TIME_ONLY = new Intl.DateTimeFormat(undefined, {
  hour: 'numeric',
  minute: '2-digit',
});

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateTime(iso: string | Date | null | undefined): string {
  const date = toDate(iso);
  return date ? DATE_TIME.format(date) : EM_DASH;
}

/**
 * A join window is almost always same-day, so the closing time is shown bare
 * rather than repeating the date the reader just read.
 */
export function formatWindow(fromIso: string | null | undefined, toIso: string | null | undefined): string {
  const from = toDate(fromIso);
  const to = toDate(toIso);
  if (!from || !to) return EM_DASH;

  const sameDay = DATE_ONLY.format(from) === DATE_ONLY.format(to);
  return sameDay
    ? `${DATE_TIME.format(from)} – ${TIME_ONLY.format(to)}`
    : `${DATE_TIME.format(from)} – ${DATE_TIME.format(to)}`;
}

/** 750000 -> "12m 30s". Used for connected time and recording length. */
export function formatDurationMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return EM_DASH;

  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

/** The scheduled length, which the API gives in whole minutes. */
export function formatMinutes(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes <= 0) return EM_DASH;
  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`;
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return EM_DASH;
  if (bytes === 0) return '0 B';

  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }

  const digits = unit === 0 ? 0 : value < 10 ? 1 : 0;
  return `${value.toFixed(digits)} ${BYTE_UNITS[unit] ?? 'B'}`;
}

const NUMBER = new Intl.NumberFormat();

export function formatCount(value: number): string {
  return NUMBER.format(value);
}

/**
 * "in 12 min" / "2 hr ago". Used for the join-link expiry so an admin can see
 * at a glance whether a candidate can still open the URL, without doing the
 * arithmetic against joinClosesAt themselves.
 */
export function formatRelativeTo(iso: string | Date | null | undefined, now = new Date()): string {
  const date = toDate(iso);
  if (!date) return EM_DASH;

  const deltaMs = date.getTime() - now.getTime();
  const past = deltaMs < 0;
  const absMin = Math.round(Math.abs(deltaMs) / 60_000);

  let span: string;
  if (absMin < 1) span = 'less than a minute';
  else if (absMin < 60) span = `${absMin} min`;
  else {
    const hours = Math.round(absMin / 60);
    span = hours === 1 ? '1 hr' : `${hours} hr`;
  }

  if (absMin < 1) return past ? 'just now' : 'any moment';
  return past ? `${span} ago` : `in ${span}`;
}
